import { forgetKept } from "./kept";
import type {
  AgreementResponse,
  AgreementUpdateRequest,
  AssetFolderResponse,
  AssetResponse,
  AssetsResponse,
  ConversionKeyResponse,
  CreateAssetFolderRequest,
  CreateCopyAssetRequest,
  CreatePartnerRequest,
  CreatePartnerResponse,
  CreateProductRequest,
  DeleteAssetResponse,
  HealthResponse,
  MarkPaidRequest,
  MarkPaidResponse,
  MeResponse,
  PartnerDetail,
  PartnerReferralsResponse,
  PartnerResponse,
  PartnersListResponse,
  PayrunResponse,
  ProductDetail,
  ProductResponse,
  ProductsListResponse,
  ProgramDetail,
  ProgramUpdateRequest,
  TrackingModeResponse,
  UpdateAssetFolderRequest,
  UpdateAssetRequest,
  UpdatePartnerRequest,
} from "../../shared/types";

// In dev, "" → relative /api (Vite proxies to the API worker). In production the
// SPA worker proxies /api to the API worker, so "" works there too. Override with
// VITE_API_URL only for a split-origin deploy.
const BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Signing in happens at Flashsuite; this starts the trip and comes back to `to`. */
export function signInUrl(to: string): string {
  return `${BASE}/api/auth/start?to=${encodeURIComponent(to)}`;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // FormData sets its own multipart content-type (with a boundary); never force JSON on it.
  const isForm = typeof FormData !== "undefined" && init?.body instanceof FormData;
  const res = await fetch(`${BASE}/api${path}`, {
    credentials: "include",
    headers: isForm ? { ...init?.headers } : { "content-type": "application/json", ...init?.headers },
    ...init,
  });
  // Why: a session ends when Flashsuite says so (Sign out everywhere), so any page can find it gone.
  if (res.status === 401 && path !== "/auth/me") window.location.assign(signInUrl(window.location.pathname + window.location.search));
  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      typeof data === "object" && data !== null && "error" in data
        ? String((data as { error: unknown }).error)
        : res.statusText;
    throw new ApiError(res.status, message);
  }
  // Why: a change may touch what any page showed, so nothing kept from before it is shown again.
  if ((init?.method ?? "GET").toUpperCase() !== "GET") forgetKept();
  return data as T;
}

export const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

const put = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: "PUT", body: JSON.stringify(body) });

const patch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: "PATCH", body: JSON.stringify(body) });

const del = <T>(path: string): Promise<T> => request<T>(path, { method: "DELETE" });

export const api = {
  // ── Auth + health (trunk) ──────────────────────────────────────────
  health: () => request<HealthResponse>("/health"),
  me: () => request<MeResponse>("/auth/me"),
  /** Ends this session; `to` ends Flashsuite's too. */
  logout: () => request<{ to: string }>("/auth/logout", { method: "POST" }),

  // ── Products ───────────────────────────────────────────────────────
  products: {
    /** Every product in the account, each with its real Partners lane. */
    list: () => request<ProductsListResponse>("/products"),
    /** One product's detail: this-month headline + the real Partners lane. */
    detail: (id: string) => request<ProductDetail>(`/products/${id}/detail`),
    /** Create a product (name required, description optional). Returns the created product. */
    create: (body: CreateProductRequest) => post<ProductResponse>("/products", body),
    /** Upload a product's logo (raw image bytes to R2). Returns the product with its logo key. */
    uploadLogo: (id: string, file: Blob) =>
      request<ProductResponse>(`/products/${id}/logo`, {
        method: "POST",
        body: file,
        headers: { "content-type": file.type || "image/jpeg" },
      }),
    /** URL of a product's served logo (an `<img>` src, same-origin so the session cookie rides along). */
    logoUrl: (id: string) => `${BASE}/api/products/${id}/logo`,
  },

  // ── Partners (owner side): program, list, invite, pay run, detail ──
  partners: {
    /** A product's partners, split active vs pending, with per-partner KPIs. */
    list: (productId: string) =>
      request<PartnersListResponse>(`/products/${productId}/partners`),
    /** Invite (create) a partner under the product's program. */
    invite: (productId: string, body: CreatePartnerRequest) =>
      post<CreatePartnerResponse>(`/products/${productId}/partners`, body),
    /** The product's reward program config (+ ladder tiers). */
    program: (productId: string) => request<ProgramDetail>(`/products/${productId}/program`),
    /** Save the product's reward program config. */
    updateProgram: (productId: string, body: ProgramUpdateRequest) =>
      put<ProgramDetail>(`/products/${productId}/program`, body),
    /** Pay run: ready to pay, needs attention (with reasons), and history. */
    payrun: (productId: string) => request<PayrunResponse>(`/products/${productId}/payrun`),
    /** One partner's full owner-side detail (KPIs, docs, referrals, owed). */
    get: (id: string) => request<PartnerDetail>(`/partners/${id}`),
    patch: (id: string, body: UpdatePartnerRequest) =>
      patch<PartnerResponse>(`/partners/${id}`, body),
    approve: (id: string) => post<PartnerResponse>(`/partners/${id}/approve`),
    reject: (id: string) => post<PartnerResponse>(`/partners/${id}/reject`),
    referrals: (id: string) => request<PartnerReferralsResponse>(`/partners/${id}/referrals`),
    /** RECORD a payment as paid (a record only; Flashadz never moves money). */
    markPaid: (id: string, body: MarkPaidRequest) =>
      post<MarkPaidResponse>(`/partners/${id}/mark-paid`, body),
  },

  // ── Agreements (owner side): the product's e-sign template + signed PDF ──
  agreements: {
    /** The product's current agreement template (clauses + fields + version). */
    get: (productId: string) => request<AgreementResponse>(`/products/${productId}/agreement`),
    /** Save the agreement builder output; bumpVersion publishes a new version (forces re-sign). */
    save: (productId: string, body: AgreementUpdateRequest) =>
      put<AgreementResponse>(`/products/${productId}/agreement`, body),
    /** URL of a partner's signed-agreement PDF (a binary download the UI links to). */
    partnerDocumentPdfUrl: (partnerId: string, docId: string) =>
      `${BASE}/api/partners/${partnerId}/documents/${docId}/pdf`,
  },

  // ── Shared asset library (owner side): folders + assets, R2-backed ──
  assets: {
    /** The product's whole library: all folders + all assets, with per-item share flags. */
    list: (productId: string) => request<AssetsResponse>(`/products/${productId}/assets`),
    /** Create a folder (optionally shared with partners on create). */
    createFolder: (productId: string, body: CreateAssetFolderRequest) =>
      post<AssetFolderResponse>(`/products/${productId}/asset-folders`, body),
    /** Add a swipe-copy asset (text stored inline, no file). */
    createCopy: (productId: string, body: CreateCopyAssetRequest) =>
      post<AssetResponse>(`/products/${productId}/assets`, body),
    /** Upload a real media/file asset to R2 (multipart). Extra fields ride alongside the file. */
    upload: (
      productId: string,
      file: File,
      fields: { name?: string; folderId?: string | null; shared?: boolean } = {},
    ) => {
      const form = new FormData();
      form.append("file", file);
      if (fields.name) form.append("name", fields.name);
      if (fields.folderId) form.append("folderId", fields.folderId);
      if (fields.shared) form.append("shared", "true");
      return request<AssetResponse>(`/products/${productId}/assets`, { method: "POST", body: form });
    },
    /** Rename, move, share-toggle, or edit swipe text on an asset. */
    patch: (assetId: string, body: UpdateAssetRequest) =>
      patch<AssetResponse>(`/assets/${assetId}`, body),
    /** Rename or share-toggle a folder. */
    patchFolder: (folderId: string, body: UpdateAssetFolderRequest) =>
      patch<AssetFolderResponse>(`/asset-folders/${folderId}`, body),
    /** Delete an asset (also removes its stored R2 object for media/file assets). */
    delete: (assetId: string) => del<DeleteAssetResponse>(`/assets/${assetId}`),
    /** Delete a folder (assets inside fall back to the product root). */
    deleteFolder: (folderId: string) => del<{ ok: boolean }>(`/asset-folders/${folderId}`),
    /** Authorized download URL for a media/file asset (the UI links to it). */
    downloadUrl: (assetId: string) => `${BASE}/api/assets/${assetId}/download`,
  },

  // ── Connections: partner sale tracking (conversion key + Stripe key) ──
  connections: {
    /** The account's sale tracking state (conversion key + Stripe key). */
    tracking: () => request<TrackingModeResponse>("/tracking"),
    /** Generate (or rotate) the secure conversion key. Returns the raw key ONCE; rotating invalidates the old one. */
    generateConversionKey: () => post<ConversionKeyResponse>("/tracking/conversion-key"),
    /** Connect Stripe with a read-only key. Returns the refreshed tracking DTO, or a 400 with why the key was rejected. */
    stripeConnect: (key: string) => post<TrackingModeResponse>("/tracking/stripe/connect", { key }),
    /** Disconnect Stripe (clear the stored key). Returns the refreshed tracking DTO. */
    stripeDisconnect: () => del<TrackingModeResponse>("/tracking/stripe"),
  },
};
